import React, { useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Voice from '@react-native-voice/voice';
import { ClassTextToSpeechPolicy, IActivity, resolveTextToSpeechPolicy, TextToSpeechPolicy } from '@eduwrite/shared';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { useAccommodationStore } from '../store/useAccommodationStore';
import { useAuthStore } from '../store/useAuthStore';
import { speakText } from './AudioPromptButton';
import { getActivityPrompt } from '../utils/activityPrompt';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';
import { SilentHelpToken } from './SilentHelpToken';
import { StrokeSyncService } from '../services/StrokeSyncService';

interface StudentLessonHeaderProps {
    activity: IActivity;
    classTextToSpeechPolicy?: ClassTextToSpeechPolicy;
    stage?: string;
    studentName?: string | null;
    taskIndex: number;
    taskCount: number;
    onBack: () => void;
    onNotifications: () => void;
    onNext?: () => void;
    onSwitchStudent: () => void;
    onExitPressIn?: () => void;
    onExitPressOut?: () => void;
    /** Hold-to-confirm progress (0→1) for the exit button. From useHoldToConfirm. */
    exitProgress?: Animated.Value;
    /** Whether the exit hold is active. From useHoldToConfirm. */
    exitIsConfirming?: boolean;
    /** WP-100: Teacher Activity Feedback trigger callback */
    onFeedbackPress?: () => void;
    /** WP-100: Explicit feedback mode override (defaults to useAuthStore.classFeedbackMode) */
    feedbackMode?: boolean;
    /** WP-102: Discreet Silent Hand-Up help request trigger */
    onRequestHelp?: () => void;
    silentHelpCooldownMs?: number;
}

const BACK_HOLD_MS = 800;
const TTS_LONG_PRESS_MS = 500;
const TABLET_WIDTH_THRESHOLD = 768;

export const StudentLessonHeader: React.FC<StudentLessonHeaderProps> = ({
    activity,
    classTextToSpeechPolicy,
    stage = 'KS1',
    studentName,
    taskIndex,
    taskCount,
    onBack,
    onNotifications,
    onNext,
    onSwitchStudent,
    onExitPressIn,
    onExitPressOut,
    exitProgress,
    exitIsConfirming,
    onFeedbackPress,
    feedbackMode,
    onRequestHelp,
    silentHelpCooldownMs,
}) => {
    const classFeedbackMode = useAuthStore((state: any) => state.classFeedbackMode);
    const isFeedbackModeActive = feedbackMode !== undefined ? feedbackMode : classFeedbackMode === true;
    const [message, setMessage] = useState<string | null>(null);
    const [backHolding, setBackHolding] = useState(false);
    const backTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // TTS short/long press state (WP-049 AC4)
    const ttsPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const ttsLongPressFired = useRef(false);

    const { width } = useWindowDimensions();
    const isTablet = width >= TABLET_WIDTH_THRESHOLD;

    const override = useAccessibilityStore((state) => state.lessonTextToSpeechOverride);
    const setOverride = useAccessibilityStore((state) => state.setLessonTextToSpeechOverride);
    const autoTtsAccommodation = useAccommodationStore((state) => state.shouldAutoTts());
    const effectiveOverride = override !== null ? override : (autoTtsAccommodation ? true : null);

    const policy = resolveTextToSpeechPolicy({
        stage,
        studentOverride: effectiveOverride,
        activityPolicy: (activity.config as { accessibility?: { textToSpeechPolicy?: TextToSpeechPolicy } })?.accessibility?.textToSpeechPolicy,
        classPolicy: classTextToSpeechPolicy,
    });
    const prompt = getActivityPrompt(activity);
    const touchTarget = stage === 'EYFS' ? 72 : stage === 'KS1' ? 64 : 56;

    const stopVoice = () => {
        if (Voice.stop) {
            void Voice.stop().catch((error: unknown) => console.warn('[StudentLessonHeader] Could not stop speech input:', error));
        }
    };

    const speak = () => {
        if (!policy.enabled) setOverride(true);
        stopVoice();
        speakText(prompt);
        setMessage('Listening help is on');
    };

    // WP-049 AC4: TTS short/long press distinction.
    // Short press while enabled → repeat prompt (speakText called).
    // Long press while enabled → disable TTS + confirmation shown.
    // Any press while disabled → enable TTS + speak prompt.
    const handleTtsPressIn = () => {
        ttsLongPressFired.current = false;
        if (policy.enabled) {
            // Start long-press timer — if it fires, disable TTS
            ttsPressTimer.current = setTimeout(() => {
                ttsLongPressFired.current = true;
                setOverride(false);
                stopVoice();
                speakText('');
                setMessage('Listening help off');
            }, TTS_LONG_PRESS_MS);
        }
        // If disabled, do nothing on pressIn — handle on pressOut (enable)
    };

    const handleTtsPressOut = () => {
        if (ttsPressTimer.current) {
            clearTimeout(ttsPressTimer.current);
            ttsPressTimer.current = null;
        }
        if (ttsLongPressFired.current) return; // Long press already handled

        if (policy.enabled) {
            // Short press while enabled → repeat prompt
            stopVoice();
            speakText(prompt);
            setMessage('Listening help is on');
        } else {
            // Short press while disabled → enable TTS
            setOverride(true);
            stopVoice();
            speakText(prompt);
            setMessage('Listening help is on');
        }
    };

    useEffect(() => () => {
        if (backTimer.current) clearTimeout(backTimer.current);
        if (ttsPressTimer.current) clearTimeout(ttsPressTimer.current);
    }, []);

    useEffect(() => {
        if (policy.autoPlay || autoTtsAccommodation) speak();
    }, [activity.id, autoTtsAccommodation]);

    const handleBackPressIn = () => {
        setBackHolding(true);
        backTimer.current = setTimeout(() => {
            setBackHolding(false);
            onBack();
        }, BACK_HOLD_MS);
    };

    const handleBackPressOut = () => {
        setBackHolding(false);
        if (backTimer.current) clearTimeout(backTimer.current);
        backTimer.current = null;
    };

    // Shared student info content — used in both tablet (centred) and narrow (pill) layouts
    const studentInfoContent = (
        <>
            <Text style={styles.infoIcon}>👤</Text>
            <Text numberOfLines={1} style={styles.studentInfo}>
                {studentName || 'Student'}
            </Text>
            <Text style={styles.infoDivider}>·</Text>
            <Text style={styles.taskProgress}>
                Task {taskIndex} of {taskCount}
            </Text>
            {getStudentSafeActivityTitle(activity) ? (
                <>
                    <Text style={styles.infoDivider}>·</Text>
                    <Text numberOfLines={1} style={styles.activityName}>
                        {getStudentSafeActivityTitle(activity)}
                    </Text>
                </>
            ) : null}
        </>
    );

    return (
        <View style={styles.container}>
            {/* Top Row — Back + Switch on left, Next + TTS + Bell (furthest right) on right */}
            <View style={styles.topRow}>
                <View style={styles.leftGroup}>
                    {/* Hold-to-confirm back button */}
                    <TouchableOpacity
                        accessibilityLabel="Back to task list"
                        accessibilityRole="button"
                        style={[styles.backButton, { minHeight: touchTarget, minWidth: touchTarget }, backHolding && styles.backButtonHolding]}
                        onPress={onBack}
                        onPressIn={handleBackPressIn}
                        onPressOut={handleBackPressOut}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.backArrow}>‹</Text>
                        <Text style={styles.backLabel}>Back</Text>
                    </TouchableOpacity>

                    {/* Switch student icon button (near Back button) */}
                    <TouchableOpacity
                        accessibilityLabel="Switch student"
                        accessibilityRole="button"
                        accessibilityHint="Switch to a different student"
                        style={[styles.iconButton, { minHeight: touchTarget, minWidth: touchTarget }]}
                        onPress={onSwitchStudent}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.icon}>👥</Text>
                    </TouchableOpacity>
                </View>

                {/* WP-049 AC5-6: On tablet, student name + progress are centred in the header */}
                {isTablet && (
                    <View testID="header-centred-info" style={styles.centredInfo}>
                        {studentInfoContent}
                    </View>
                )}

                <View style={styles.rightGroup}>
                    {onNext && (
                        <TouchableOpacity
                            accessibilityLabel="Next task"
                            accessibilityRole="button"
                            style={[styles.utilityButton, { minHeight: touchTarget, minWidth: touchTarget }]}
                            onPress={onNext}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.utilityText}>Next</Text>
                        </TouchableOpacity>
                    )}

                    {/* WP-102: Discreet Silent Hand-Up / Teacher Check-in Token */}
                    <SilentHelpToken
                        stage={stage}
                        onRequestHelp={onRequestHelp || (() => StrokeSyncService.requestHelp())}
                        cooldownMs={silentHelpCooldownMs}
                    />

                    {/* WP-049 AC4: TTS Listening Help — short press repeats prompt, long press disables */}
                    <TouchableOpacity
                        accessibilityLabel={policy.enabled ? 'Turn off listening help' : 'Turn on listening help'}
                        accessibilityRole="button"
                        style={[
                            styles.iconButton,
                            { minHeight: touchTarget, minWidth: touchTarget },
                            policy.enabled ? styles.ttsButton : styles.offButton,
                        ]}
                        onPressIn={handleTtsPressIn}
                        onPressOut={handleTtsPressOut}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.icon}>{policy.enabled ? '🔊' : '🔇'}</Text>
                    </TouchableOpacity>

                    {/* WP-100: Teacher Feedback trigger (visible only when feedback mode is active) */}
                    {isFeedbackModeActive && (
                        <TouchableOpacity
                            testID="teacher-feedback-btn"
                            accessibilityLabel="Teacher feedback"
                            accessibilityRole="button"
                            accessibilityHint="Open teacher feedback dialog for this activity"
                            style={[
                                styles.feedbackButton,
                                { minHeight: touchTarget, minWidth: touchTarget },
                            ]}
                            onPress={onFeedbackPress}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.feedbackIcon}>👩‍🏫</Text>
                            <Text style={styles.feedbackLabel}>Feedback</Text>
                        </TouchableOpacity>
                    )}

                    {/* Notification Bell (furthest right) */}
                    <TouchableOpacity
                        accessibilityLabel="Notifications"
                        accessibilityRole="button"
                        style={[styles.iconButton, { minHeight: touchTarget, minWidth: touchTarget }]}
                        onPress={onNotifications}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.icon}>🔔</Text>
                    </TouchableOpacity>
                </View>
            </View>

            {/* Second Row — Hold to Leave on its own row */}
            {onExitPressIn && onExitPressOut && (
                <View style={styles.exitRow}>
                    <TouchableOpacity
                        accessibilityLabel="Hold to leave lesson"
                        accessibilityRole="button"
                        style={[styles.exitButton, { minHeight: touchTarget, minWidth: touchTarget }]}
                        onPressIn={onExitPressIn}
                        onPressOut={onExitPressOut}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.exitText}>Hold to leave</Text>
                        {/* WP-049 AC2: progress bar + "Keep holding" text during the hold */}
                        {exitIsConfirming ? (
                            <Text style={styles.holdHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Keep holding to leave…</Text>
                        ) : null}
                        {exitIsConfirming && exitProgress ? (
                            <Animated.View style={[styles.holdBar, { width: exitProgress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }]} />
                        ) : null}
                    </TouchableOpacity>
                </View>
            )}

            {/* WP-049 AC5-6: Narrow phone layout — compact info pill at bottom */}
            {!isTablet && (
                <View style={styles.bottomRow}>
                    <View testID="header-compact-info" style={styles.infoPill}>
                        {studentInfoContent}
                    </View>
                </View>
            )}

            {/* Status confirmation feedback */}
            {message && <Text accessibilityLiveRegion="polite" style={styles.message}>{message}</Text>}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#FFFFFF',
        borderBottomColor: '#E2E8F0',
        borderBottomWidth: 1,
        paddingHorizontal: 12,
        paddingTop: 6,
        paddingBottom: 6,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 3,
        elevation: 2,
        zIndex: 10,
    },
    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    leftGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    rightGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    centredInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 6,
    },
    exitRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 6,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FF9800',
        borderColor: '#FBBF24',
        borderBottomColor: '#D97706',
        borderBottomWidth: 4,
        borderRadius: 20,
        borderWidth: 1.5,
        paddingHorizontal: 14,
        gap: 4,
        shadowColor: '#D97706',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 4,
    },
    backButtonHolding: {
        backgroundColor: '#EA580C',
        borderColor: '#C2410C',
        borderBottomColor: '#9A3412',
    },
    backArrow: {
        color: '#FFFFFF',
        fontSize: 28,
        fontWeight: '900',
        marginTop: -3,
    },
    backLabel: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    iconButton: {
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderColor: '#E2E8F0',
        borderBottomColor: '#CBD5E1',
        borderBottomWidth: 3.5,
        borderRadius: 18,
        borderWidth: 1.5,
        justifyContent: 'center',
        minHeight: 56,
        minWidth: 56,
        paddingHorizontal: 10,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
        elevation: 2,
    },
    ttsButton: {
        backgroundColor: '#EEF2FF',
        borderColor: '#C7D2FE',
        borderBottomColor: '#818CF8',
    },
    offButton: {
        backgroundColor: '#F8FAFC',
        borderColor: '#E2E8F0',
        borderBottomColor: '#CBD5E1',
    },
    icon: {
        fontSize: 20,
    },
    utilityButton: {
        alignItems: 'center',
        backgroundColor: '#22C55E',
        borderColor: '#4ADE80',
        borderBottomColor: '#15803D',
        borderBottomWidth: 3.5,
        borderRadius: 16,
        borderWidth: 1.5,
        justifyContent: 'center',
        paddingHorizontal: 14,
        shadowColor: '#15803D',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 3,
    },
    utilityText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    exitButton: {
        alignItems: 'center',
        backgroundColor: '#FFF7ED',
        borderColor: '#C2410C',
        borderRadius: 10,
        borderWidth: 2,
        justifyContent: 'center',
        paddingHorizontal: 12,
    },
    exitText: {
        color: '#9A3412',
        fontSize: 13,
        fontWeight: '800',
    },
    holdHint: {
        fontSize: 12,
        color: '#F59E0B',
        marginTop: 2,
        textAlign: 'center',
    },
    holdBar: {
        height: 4,
        backgroundColor: '#F59E0B',
        borderRadius: 2,
        marginTop: 4,
    },
    bottomRow: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 6,
    },
    infoPill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F1F5F9',
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 3,
        maxWidth: '100%',
        gap: 6,
    },
    infoIcon: {
        fontSize: 12,
    },
    studentInfo: {
        color: '#0F172A',
        fontSize: 12,
        fontWeight: '800',
    },
    infoDivider: {
        color: '#94A3B8',
        fontSize: 12,
        fontWeight: 'bold',
    },
    taskProgress: {
        color: '#475569',
        fontSize: 12,
        fontWeight: '700',
    },
    activityName: {
        color: '#64748B',
        fontSize: 12,
        fontWeight: '600',
        flexShrink: 1,
    },
    message: {
        color: '#4338CA',
        fontSize: 12,
        fontWeight: '700',
        marginTop: 4,
        textAlign: 'center',
    },
    feedbackButton: {
        backgroundColor: '#EEF2FF',
        borderRadius: 9999,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        borderWidth: 1.5,
        borderColor: '#818CF8',
        gap: 6,
        justifyContent: 'center',
    },
    feedbackIcon: {
        fontSize: 16,
    },
    feedbackLabel: {
        fontSize: 13,
        fontWeight: '800',
        color: '#4F46E5',
    },
});
